import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ShieldOff } from 'lucide-react';
import { Button } from './ui/button';

// A full-panel lock card shown in place of a feature locked to a paid plan.
// Used for Contact Support page, Integrations tab, Burndown tab, etc.
//
// `mode`:
//   'plan_locked'    → "Upgrade to Business" CTA (default for free-plan locks)
//   'admin_disabled' → "Disabled by your account manager" message (no upgrade CTA)
export function UpgradeLockCard({
  title,
  description,
  featureName,
  mode = 'plan_locked',
  testId = 'upgrade-lock-card',
}) {
  const navigate = useNavigate();
  const adminDisabled = mode === 'admin_disabled';
  const heading = title || (adminDisabled
    ? 'Feature unavailable'
    : 'Upgrade to unlock this feature');
  const body = description || (adminDisabled
    ? 'This feature has been disabled for your workspace. Contact your administrator or our support team if you need access.'
    : 'This feature is available on the Business plan. Upgrade your workspace to continue.');

  return (
    <div className="flex-1 flex items-center justify-center p-8" data-testid={testId}>
      <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
        <div className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${
          adminDisabled
            ? 'bg-gradient-to-br from-slate-500 to-slate-700'
            : 'bg-gradient-to-br from-amber-400 to-amber-600'
        }`}>
          {adminDisabled ? <ShieldOff className="w-6 h-6 text-white" /> : <Lock className="w-6 h-6 text-white" />}
        </div>
        <h2 className="text-lg font-bold text-slate-900">{heading}</h2>
        {featureName && <p className="text-xs text-slate-400 mt-1 font-mono">{featureName}</p>}
        <p className="text-sm text-slate-500 mt-2 leading-relaxed">{body}</p>
        {adminDisabled ? (
          <Button
            onClick={() => navigate('/contact-support')}
            variant="outline"
            className="mt-5 h-10 rounded-xl"
            data-testid={`${testId}-contact`}
          >
            Contact support
          </Button>
        ) : (
          <Button
            onClick={() => navigate('/billing')}
            className="mt-5 h-10 rounded-xl bg-slate-900 hover:bg-slate-800"
            data-testid={`${testId}-upgrade`}
          >
            <Sparkles className="w-4 h-4 mr-1.5" /> Upgrade to Business
          </Button>
        )}
      </div>
    </div>
  );
}

// A small lock chip to place after labels like sidebar nav items / tabs so
// users know the feature is paywalled before they click.
export function LockPill({ label = 'Pro', className = '' }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-amber-400/20 text-amber-300 ${className}`}
      data-testid="lock-pill"
    >
      <Lock className="w-2.5 h-2.5" /> {label}
    </span>
  );
}
